import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Box,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { useTranslation } from 'react-i18next';
import apiService from '../../api/apiService';
import { useRefetchTrigger } from '../../hooks/useRefetchTrigger';
import dayjs from 'dayjs';

const RISK_LEVEL_KEYS = {
    Low: 'riskLevel.low',
    Medium: 'riskLevel.medium',
    High: 'riskLevel.high',
    'Very High': 'riskLevel.veryHigh',
};

const RISK_LEVELS = [
    { key: 'Low', color: '#99C20E' },
    { key: 'Medium', color: '#f4c430' },
    { key: 'High', color: '#f28c28' },
    { key: 'Very High', color: '#df3f3f' },
];

const FILTER_TYPES = {
    NONE: '',
    LOCATION: 'location',
};

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';

const normalizeDateValue = (value) => {
    if (!value) {
        return '';
    }

    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
};

const TOOLTIP_WIDTH = 160;
const TOOLTIP_HEIGHT = 84;
const TOOLTIP_OFFSET = 14;
const EMPTY_RISK_PROFILE = {
    data: [],
    total: 0,
    averageRiskLevel: null,
};

const RiskTooltip = ({ item, mousePosition, chartRefs, localizeRiskKey }) => {
    const tooltipItem = item?.payload ?? item;
    const chartKey = item?.chartKey ?? tooltipItem?.chartKey;

    if (!tooltipItem || !mousePosition.x || typeof document === 'undefined') {
        return null;
    }

    const chartBounds = chartRefs.current[chartKey]?.getBoundingClientRect();
    const chartCenterX = chartBounds ? chartBounds.left + chartBounds.width / 2 : window.innerWidth / 2;
    const chartCenterY = chartBounds ? chartBounds.top + chartBounds.height / 2 : window.innerHeight / 2;
    const placeLeft = mousePosition.x >= chartCenterX;
    const placeAbove = mousePosition.y >= chartCenterY;
    const tooltipX = placeLeft
        ? Math.max(12, mousePosition.x - TOOLTIP_WIDTH - TOOLTIP_OFFSET)
        : Math.min(window.innerWidth - TOOLTIP_WIDTH - 12, mousePosition.x + TOOLTIP_OFFSET);
    const tooltipY = placeAbove
        ? Math.max(12, mousePosition.y - TOOLTIP_HEIGHT - TOOLTIP_OFFSET)
        : Math.min(window.innerHeight - TOOLTIP_HEIGHT - 12, mousePosition.y + TOOLTIP_OFFSET);

    return ReactDOM.createPortal(
        <Box
            sx={{
                position: 'fixed',
                top: tooltipY,
                left: tooltipX,
                width: TOOLTIP_WIDTH,
                borderRadius: '12px',
                border: '1px solid rgba(31, 42, 55, 0.12)',
                backgroundColor: '#ffffff',
                boxShadow: '0 18px 44px rgba(15, 23, 42, 0.18)',
                px: 1.5,
                py: 1.25,
                zIndex: 9999,
                pointerEvents: 'none'
            }}
        >
            <Typography sx={{ fontSize: 12, color: '#71808c', fontWeight: 800 }}>
                {localizeRiskKey(tooltipItem.key)}
            </Typography>
            <Typography sx={{ fontSize: 22, lineHeight: 1.05, color: '#1d2a32', fontWeight: 900 }}>
                {tooltipItem.count}
            </Typography>
        </Box>,
        document.body
    );
};

const renderActiveShape = (props) => (
    <Sector
        {...props}
        outerRadius={props.outerRadius + 4}
        stroke={props.fill}
        strokeWidth={1}
        style={{
            cursor: 'pointer',
            filter: 'drop-shadow(0 8px 12px rgba(15, 23, 42, 0.28))',
            transition: 'none'
        }}
    />
);

const DashboardRiskLevel = ({ jsonSettings }) => {
    const { t } = useTranslation();
    const refetchTrigger = useRefetchTrigger();
    const [riskProfiles, setRiskProfiles] = useState({
        uncontrolled: EMPTY_RISK_PROFILE,
        controlled: EMPTY_RISK_PROFILE,
        earlierSnapshot: EMPTY_RISK_PROFILE,
        laterSnapshot: EMPTY_RISK_PROFILE
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTooltipItem, setActiveTooltipItem] = useState(null);
    const [activeSlice, setActiveSlice] = useState(null);
    const [tooltipMousePosition, setTooltipMousePosition] = useState({ x: 0, y: 0 });
    const chartRef = useRef(null);
    const chartRefs = useRef({});

    const parseSettings = (settings) => {
        try {
            if (!settings || settings === '{}')
                return {};

            return typeof settings === 'string' ? JSON.parse(settings) : settings;
        }
        catch (error) {
            console.error('Error parsing jsonSettings:', error);
            return {};
        }
    };

    const settings = parseSettings(jsonSettings);
    const filterType = settings.filterType === FILTER_TYPES.LOCATION || settings.selectedLocationName
        ? FILTER_TYPES.LOCATION
        : FILTER_TYPES.NONE;
    const selectedLocationName = filterType === FILTER_TYPES.LOCATION ? settings.selectedLocationName ?? '' : '';
    const selectedStartDate = normalizeDateValue(settings.startDate);
    const selectedEndDate = normalizeDateValue(settings.endDate);
    const hasSnapshotComparison = Boolean(selectedStartDate && selectedEndDate);
    const snapshotDates = useMemo(() => {
        return {
            baseline: selectedStartDate,
            comparison: selectedEndDate
        };
    }, [selectedStartDate, selectedEndDate]);

    useEffect(() => {
        void loadHazardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jsonSettings, refetchTrigger]);

    const getRiskSummaryApiUrl = () => {
        const queryParams = new URLSearchParams();

        if (filterType === FILTER_TYPES.LOCATION && selectedLocationName) {
            queryParams.set('locationName', selectedLocationName);
        }

        if (hasSnapshotComparison) {
            queryParams.set('baselineDate', snapshotDates.baseline);
            queryParams.set('comparisonDate', snapshotDates.comparison);
        }

        const queryString = queryParams.toString();
        return queryString ? `api/Hazard/riskLevelSummary?${queryString}` : 'api/Hazard/riskLevelSummary';
    };

    const loadHazardData = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getData(getRiskSummaryApiUrl());
            const profiles = response.data.data?.profiles ?? [];
            const profilesByKey = profiles.reduce((result, profile) => ({
                ...result,
                [profile.key]: profile
            }), {});

            setRiskProfiles({
                uncontrolled: profilesByKey.uncontrolled ?? EMPTY_RISK_PROFILE,
                controlled: profilesByKey.controlled ?? EMPTY_RISK_PROFILE,
                earlierSnapshot: profilesByKey.earlierSnapshot ?? EMPTY_RISK_PROFILE,
                laterSnapshot: profilesByKey.laterSnapshot ?? EMPTY_RISK_PROFILE
            });
        }
        catch (error) {
            console.error('Error loading hazard data:', error);
            setError(t('common.errorWithMessage', { message: error.message ?? t('dashboardRiskLevel.empty.noHazards') }));
        }
        finally {
            setLoading(false);
        }
    };

    const localizeRiskKey = (key) => t(RISK_LEVEL_KEYS[key] ?? key);
    const getRiskColor = (key) => RISK_LEVELS.find((level) => level.key === key)?.color ?? '#99C20E';
    const dateFilterSummary = hasSnapshotComparison
        ? t('dashboardRiskLevel.summary.snapshotComparison', {
            startDate: dayjs(snapshotDates.baseline).format(DATE_DISPLAY_FORMAT),
            endDate: dayjs(snapshotDates.comparison).format(DATE_DISPLAY_FORMAT)
        })
        : '';
    const filterSummaryParts = [
        filterType === FILTER_TYPES.LOCATION && selectedLocationName
            ? t('dashboardRiskLevel.summary.filteredByLocation', { name: selectedLocationName })
            : '',
        dateFilterSummary
    ].filter(Boolean);
    const filterSummary = filterSummaryParts.length > 0
        ? filterSummaryParts.join(' | ')
        : t('dashboardRiskLevel.summary.noFilter');
    const chartProfiles = useMemo(() => (
        hasSnapshotComparison
            ? [
                {
                    key: 'earlierSnapshot',
                    title: t('dashboardRiskLevel.button.controlledAsOf', { date: dayjs(snapshotDates.baseline).format(DATE_DISPLAY_FORMAT) }),
                    profile: riskProfiles.earlierSnapshot ?? EMPTY_RISK_PROFILE
                },
                {
                    key: 'laterSnapshot',
                    title: t('dashboardRiskLevel.button.controlledAsOf', { date: dayjs(snapshotDates.comparison).format(DATE_DISPLAY_FORMAT) }),
                    profile: riskProfiles.laterSnapshot ?? EMPTY_RISK_PROFILE
                }
            ]
            : [
                { key: 'uncontrolled', title: t('dashboardRiskLevel.button.uncontrolled'), profile: riskProfiles.uncontrolled },
                { key: 'controlled', title: t('dashboardRiskLevel.button.controlled'), profile: riskProfiles.controlled }
            ]
    ), [hasSnapshotComparison, riskProfiles, snapshotDates, t]);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                borderRadius: '14px',
                background: 'transparent',
                p: 2.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
            }}
        >
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant='h6' sx={{ fontWeight: 900, lineHeight: 1.1, color: '#1d2a32' }}>
                        {t('dashboardRiskLevel.title')}
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 12, color: '#667681', fontWeight: 600 }}>
                        {filterSummary}
                    </Typography>
                </Box>
            </Stack>

            <Box
                ref={chartRef}
                onMouseMove={(event) => setTooltipMousePosition({ x: event.clientX, y: event.clientY })}
                onMouseLeave={() => {
                    setActiveTooltipItem(null);
                    setActiveSlice(null);
                }}
                sx={{ flex: 1, minHeight: 190, position: 'relative' }}
            >
                {loading ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%' }}>
                        <CircularProgress size={30} sx={{ color: '#99C20E' }} />
                    </Stack>
                ) : error ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%', textAlign: 'center', px: 2 }}>
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Stack>
                ) : chartProfiles.every(({ profile }) => profile.total === 0) ? (
                    <Stack alignItems='center' justifyContent='center' sx={{ height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            {t('dashboardRiskLevel.empty.noHazards')}
                        </Typography>
                    </Stack>
                ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ height: '100%' }}>
                        {chartProfiles.map(({ key, title, profile }) => (
                            <Box
                                key={key}
                                ref={(element) => {
                                    chartRefs.current[key] = element;
                                }}
                                sx={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}
                            >
                                <Stack alignItems='center' spacing={0.25} sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: '#1d2a32', fontWeight: 900 }}>
                                        {title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: profile.averageRiskLevel ? getRiskColor(profile.averageRiskLevel) : '#71808c', fontWeight: 800 }}>
                                        {t('dashboardRiskLevel.metric.averageRiskLevel')}: {profile.averageRiskLevel ? localizeRiskKey(profile.averageRiskLevel) : t('common.none')}
                                    </Typography>
                                </Stack>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <PieChart margin={{ top: 28, right: 8, bottom: 8, left: 8 }}>
                                        <Pie
                                            dataKey='count'
                                            data={profile.data}
                                            cx='50%'
                                            cy='50%'
                                            innerRadius='58%'
                                            outerRadius='82%'
                                            paddingAngle={3}
                                            cornerRadius={8}
                                            stroke='#ffffff'
                                            strokeWidth={3}
                                            activeIndex={activeSlice?.chartKey === key ? activeSlice.index : undefined}
                                            activeShape={renderActiveShape}
                                            isAnimationActive={false}
                                            onMouseEnter={(entry, index) => {
                                                setActiveTooltipItem({ ...entry, chartKey: key });
                                                setActiveSlice({ chartKey: key, index });
                                            }}
                                            onMouseMove={(entry, index) => {
                                                setActiveTooltipItem({ ...entry, chartKey: key });
                                                setActiveSlice({ chartKey: key, index });
                                            }}
                                            onMouseLeave={() => {
                                                setActiveTooltipItem(null);
                                                setActiveSlice(null);
                                            }}
                                        >
                                            {profile.data.map((entry) => (
                                                <Cell key={`${key}-${entry.key}`} fill={getRiskColor(entry.key)} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <Stack
                                    alignItems='center'
                                    justifyContent='center'
                                    sx={{
                                        position: 'absolute',
                                        inset: '50% auto auto 50%',
                                        transform: 'translate(-50%, -50%)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <Typography sx={{ fontSize: 30, lineHeight: 1, color: '#1d2a32', fontWeight: 900 }}>
                                        {profile.total}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#71808c', fontWeight: 800 }}>
                                        {t('dashboardRiskLevel.metric.totalHazards')}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
                <RiskTooltip
                    item={activeTooltipItem}
                    mousePosition={tooltipMousePosition}
                    chartRefs={chartRefs}
                    localizeRiskKey={localizeRiskKey}
                />
            </Box>

            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {RISK_LEVELS.map((level) => {
                    return (
                        <Box
                            key={level.key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                minWidth: 0,
                                fontSize: 12,
                                color: '#52616b',
                                fontWeight: 800
                            }}
                        >
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: level.color, flexShrink: 0 }} />
                            <Box component='span'>{localizeRiskKey(level.key)}</Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};
export default DashboardRiskLevel;
